import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, Image as ImageIcon, ShieldCheck, FloppyDisk, PaperPlaneTilt, FilmSlate } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PLATFORMS } from "../lib/meta";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Explainer, Jargon } from "../components/friendly";

export default function ComposerPage() {
  const { activeClientId, activeClient, refreshOrg } = useApp();
  const location = useLocation();
  const [mode, setMode] = useState("ai");
  const [platforms, setPlatforms] = useState(["instagram", "linkedin"]);
  const [brief, setBrief] = useState(location.state?.brief || "");
  const [tone, setTone] = useState("");
  const [language, setLanguage] = useState("");
  const [variants, setVariants] = useState(null);
  const [activeTab, setActiveTab] = useState("instagram");
  const [scheduled, setScheduled] = useState("");
  const [imgPrompt, setImgPrompt] = useState("");
  const [images, setImages] = useState({});
  const [violations, setViolations] = useState(null);
  const [busy, setBusy] = useState("");
  const [mPlatform, setMPlatform] = useState("instagram");
  const [mCaption, setMCaption] = useState("");
  const [videos, setVideos] = useState({});

  const toggle = (p) => setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const generate = async () => {
    if (!brief.trim()) return toast.error("Write a brief first");
    if (!platforms.length) return toast.error("Select at least one platform");
    setBusy("gen");
    try {
      const { data } = await api.post("/ai/generate", { client_id: activeClientId, platforms, brief, tone, language });
      setVariants(data.variants);
      setActiveTab(platforms[0]);
      setImgPrompt(data.variants[platforms[0]]?.image_idea || "");
      refreshOrg();
      toast.success("Drafts generated (1 credit)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setBusy(""); }
  };

  const genImage = async () => {
    if (!imgPrompt.trim()) return toast.error("Describe the image first");
    setBusy("img");
    try {
      const { data } = await api.post("/ai/generate-image", { client_id: activeClientId, prompt: imgPrompt, platform: activeTab });
      setImages({ ...images, [activeTab]: data.url });
      refreshOrg();
      toast.success("Image generated & saved to Assets (3 credits)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image generation failed");
    } finally { setBusy(""); }
  };

  const genVideo = async () => {
    if (!imgPrompt.trim()) return toast.error("Describe the visual first");
    setBusy("vid");
    try {
      const { data } = await api.post("/ai/generate-video", { client_id: activeClientId, prompt: imgPrompt, platform: activeTab });
      setVideos({ ...videos, [activeTab]: { url: data.url, mock: data.mock } });
      refreshOrg();
      toast.success(data.mock ? "Mock video preview attached (free — add a FAL key to go live)" : "Video generated (15 credits)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Video generation failed");
    } finally { setBusy(""); }
  };

  const checkSafeguards = async () => {
    const text = variants?.[activeTab]?.caption || "";
    if (!text) return;
    const { data } = await api.post("/safeguards/check", { client_id: activeClientId, text });
    setViolations(data.violations);
    data.passed ? toast.success("Safeguards passed") : toast.warning(`${data.violations.length} issue(s) found`);
  };

  const save = async (submit) => {
    if (!variants) return;
    setBusy("save");
    try {
      for (const p of platforms) {
        if (!variants[p]?.caption) continue;
        const { data: draft } = await api.post("/drafts", {
          client_id: activeClientId, platform: p, caption: variants[p].caption,
          image_url: images[p] || "", scheduled_date: scheduled, ai_generated: true,
        });
        if (submit) await api.post(`/drafts/${draft.draft_id}/submit`, {});
      }
      toast.success(submit ? "Sent to Approval Inbox" : "Saved as drafts");
      setVariants(null); setImages({}); setBrief(""); setViolations(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  };

  const updateCaption = (p, v) => setVariants({ ...variants, [p]: { ...variants[p], caption: v } });

  const saveManual = async (submit) => {
    if (!mCaption.trim()) return toast.error("Write your caption first");
    setBusy("save");
    try {
      const { data: draft } = await api.post("/drafts", {
        client_id: activeClientId, platform: mPlatform, caption: mCaption,
        image_url: images[mPlatform] || "", scheduled_date: scheduled, ai_generated: false,
      });
      if (submit) await api.post(`/drafts/${draft.draft_id}/submit`, {});
      toast.success(submit ? "Sent to Approval Inbox" : "Saved as draft");
      setMCaption(""); setViolations(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  };

  const checkManualSafeguards = async () => {
    if (!mCaption) return;
    const { data } = await api.post("/safeguards/check", { client_id: activeClientId, text: mCaption });
    setViolations(data.violations);
    data.passed ? toast.success("Safeguards passed") : toast.warning(`${data.violations.length} issue(s) found`);
  };

  return (
    <div className="space-y-6 fade-up max-w-4xl" data-testid="composer-page">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Composer</h1>
        <p className="text-sm text-slate-500 mt-1">AI drafts grounded in <b>{activeClient?.name}</b>'s Brand Intelligence. Text ≈1 <Jargon tip="Credits are the app's currency for AI work. Your balance is always visible top-right; failed generations are refunded automatically.">credit</Jargon> · Image ≈3.</p>
      </div>
      <Explainer id="composer">Describe what you want to say once — the co-pilot writes a tailored version for each platform, in your brand's voice, within character limits. You always review and edit; nothing moves forward without you.</Explainer>

      <div className="inline-flex bg-slate-100 rounded-full p-1">
        {[["ai", "AI Co-Pilot"], ["manual", "Manual"]].map(([m, label]) => (
          <button key={m} data-testid={`composer-mode-${m}`} onClick={() => { setMode(m); setViolations(null); }}
            className={`px-4 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${mode === m ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
            {label}
          </button>
        ))}
      </div>

      {mode === "ai" && (
      <div className="bg-white border border-slate-200/80 rounded-xl p-6 card-float space-y-4">
        <div className="flex flex-wrap gap-2">
          {Object.entries(PLATFORMS).map(([k, p]) => (
            <button key={k} data-testid={`platform-chip-${k}`} onClick={() => toggle(k)}
              className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${platforms.includes(k) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <Textarea data-testid="composer-brief-input" value={brief} onChange={(e) => setBrief(e.target.value)} rows={3}
          placeholder={`What should we post about? e.g. "Announce the new single-origin drop launching Thursday"`} />
        <div className="flex flex-col sm:flex-row gap-3">
          <Input data-testid="composer-tone-input" value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Tone override (optional)" className="sm:max-w-[190px]" />
          <Input data-testid="composer-language-input" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="Language (default English)" className="sm:max-w-[180px]" />
          <Input data-testid="composer-date-input" type="date" value={scheduled} onChange={(e) => setScheduled(e.target.value)} className="sm:max-w-[160px]" />
          <Button data-testid="composer-generate-button" onClick={generate} disabled={busy === "gen"} className="bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)] rounded-lg gap-2 sm:ml-auto">
            <Sparkle size={15} weight="fill" /> {busy === "gen" ? "Generating…" : "Generate (1 credit)"}
          </Button>
        </div>
      </div>
      )}

      {mode === "manual" && (
        <div className="bg-white border border-slate-200/80 rounded-xl p-6 card-float space-y-4" data-testid="manual-composer">
          <div className="flex flex-wrap gap-2">
            {Object.entries(PLATFORMS).map(([k, p]) => (
              <button key={k} data-testid={`manual-platform-${k}`} onClick={() => setMPlatform(k)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${mPlatform === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <Textarea data-testid="manual-caption-input" rows={7} value={mCaption} onChange={(e) => setMCaption(e.target.value)}
            placeholder={`Write your ${PLATFORMS[mPlatform].label} post…`} />
          <p className={`text-[11px] ${mCaption.length > PLATFORMS[mPlatform].charLimit ? "text-red-500 font-semibold" : "text-slate-400"}`}>
            {mCaption.length} / {PLATFORMS[mPlatform].charLimit} characters
          </p>
          {violations && violations.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-1">
              {violations.map((v, i) => <p key={i} className="text-[12px] text-orange-700">⚠ {v.detail}</p>)}
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <Input data-testid="manual-date-input" type="date" value={scheduled} onChange={(e) => setScheduled(e.target.value)} className="max-w-[160px]" />
            <Button data-testid="manual-safeguards-button" variant="outline" onClick={checkManualSafeguards} className="gap-2 rounded-lg"><ShieldCheck size={15} /> Safeguards</Button>
            <Button data-testid="manual-save-button" variant="outline" onClick={() => saveManual(false)} disabled={busy === "save"} className="gap-2 rounded-lg ml-auto"><FloppyDisk size={15} /> Save draft</Button>
            <Button data-testid="manual-submit-button" onClick={() => saveManual(true)} disabled={busy === "save"} className="gap-2 rounded-lg bg-slate-900 hover:bg-slate-700"><PaperPlaneTilt size={15} /> Submit for approval</Button>
          </div>
        </div>
      )}

      {mode === "ai" && variants && (
        <div className="bg-white border border-slate-200/80 rounded-xl p-6 card-float space-y-4">
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setImgPrompt(variants[v]?.image_idea || ""); setViolations(null); }}>
            <TabsList className="h-auto flex-wrap">
              {platforms.map((p) => <TabsTrigger key={p} value={p} data-testid={`variant-tab-${p}`}>{PLATFORMS[p].label}</TabsTrigger>)}
            </TabsList>
            {platforms.map((p) => (
              <TabsContent key={p} value={p} className="space-y-3">
                <Textarea data-testid={`variant-caption-${p}`} rows={7} value={variants[p]?.caption || ""} onChange={(e) => updateCaption(p, e.target.value)} />
                <p className={`text-[11px] ${(variants[p]?.caption || "").length > PLATFORMS[p].charLimit ? "text-red-500 font-semibold" : "text-slate-400"}`}>
                  {(variants[p]?.caption || "").length} / {PLATFORMS[p].charLimit} characters
                </p>
                {images[p] && <img src={fileUrl(images[p])} alt="Generated" className="rounded-lg border border-slate-200 max-h-64" />}
                {videos[p] && (
                  <div className="relative inline-block">
                    <video data-testid={`variant-video-${p}`} src={videos[p].url} controls className="rounded-lg border border-slate-200 max-h-64" />
                    {videos[p].mock && <span className="absolute top-2 left-2 bg-amber-500 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">Mock preview</span>}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>

          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input data-testid="image-prompt-input" value={imgPrompt} onChange={(e) => setImgPrompt(e.target.value)} placeholder="Describe the visual…" />
              <Button data-testid="generate-image-button" variant="outline" onClick={genImage} disabled={busy === "img"} className="gap-2 rounded-lg shrink-0">
                <ImageIcon size={15} /> {busy === "img" ? "Creating…" : "AI Image (3 credits)"}
              </Button>
              <Button data-testid="generate-video-button" variant="outline" onClick={genVideo} disabled={busy === "vid"} className="gap-2 rounded-lg shrink-0">
                <FilmSlate size={15} /> {busy === "vid" ? "Rendering…" : "AI Video (15 cr · mock free)"}
              </Button>
            </div>
            {violations && violations.length > 0 && (
              <div data-testid="safeguard-violations" className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-1">
                {violations.map((v, i) => <p key={i} className="text-[12px] text-orange-700">⚠ {v.detail}</p>)}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button data-testid="safeguards-check-button" variant="outline" onClick={checkSafeguards} className="gap-2 rounded-lg"><ShieldCheck size={15} /> Safeguards check</Button>
              <Button data-testid="save-draft-button" variant="outline" onClick={() => save(false)} disabled={busy === "save"} className="gap-2 rounded-lg ml-auto"><FloppyDisk size={15} /> Save drafts</Button>
              <Button data-testid="submit-approval-button" onClick={() => save(true)} disabled={busy === "save"} className="gap-2 rounded-lg bg-slate-900 hover:bg-slate-700"><PaperPlaneTilt size={15} /> Submit for approval</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
